<template>
  <div ref="dragImageRef" class="drag-ghost-image" aria-hidden="true">
    <i class="fa-regular fa-file-lines" />
    <span ref="dragImageFileNameRef" class="drag-ghost-image__filename" />
  </div>

  <div ref="dropZoneRef" class="document-card-body-drop-zone" @dragenter.prevent="onDragEnter" @dragleave="onDragLeave" @dragover.prevent="onDragOver"
    @drop.prevent="onDrop">
    <div v-if="showDropOverlay" class="document-card-body-drop-overlay" aria-hidden="true">
      <span class="document-card-body-drop-overlay__message">{{ $t("documentCard.dropHereToAdd") }}</span>
    </div>

    <input type="file" class="d-none" ref="fileUploader" @change="onFileInputChange" :accept="acceptString" />
    <div v-if="props.templateUri" class="d-flex justify-content-center mb-3">
      <DxButton icon="download" :text="$t('documentCard.downloadButtonText')" @click="downloadTemplate" />
    </div>
    <div v-if="props.types && props.types.length > 1" class="d-flex justify-content-center mb-3">
      <DxSelectBox v-bind="selectBoxProps" v-model:value="selectedTypeCode" @value-changed="onSelectedTypeCodeChanged"
        :placeholder="$t('documentCard.chooseDocumentType')" />
    </div>
    <div v-if="props.actions.includes('add') && canAddFile" class="add-icon-container-wrapper">
      <div class="add-icon-container" @click="onAddDocument">
        <i class="fa fa-plus" />
      </div>
    </div>
    <div class="document-file-list-container">
      <template v-for="(file, fileId) in filesObject" :key="fileId">
        <div v-if="file.status !== 'deleted'" class="fila-archivos" :class="{ 'fila-archivos--draggable': props.draggable }">
          <div class="iconos-izquieda ">
            <a v-if="props.actions.includes('open')" @click="openDocument(file)" class="btn-link"> <i class="fa-regular fa-eye fa-lg" /> </a>

            <a v-if="props.actions.includes('download') && file.remoteUri && file.status === 'unchanged'" @click="downloadDocument(file)" class="btn-link">
              <i class="fa-regular fa-download fa-lg"></i>
            </a>
            <a :id="`${popoverInstanceId}_popover_${file.id}`" class="btn-link">
              <i v-if="file.description" class="fa-regular fa-circle-info fa-lg" />
              <DxPopover :hide-on-outside-click="true" :target="`#${popoverInstanceId}_popover_${file.id}`" show-event="click mouseenter" hide-event="mouseout">
                {{ file.description }}
              </DxPopover>
            </a>
          </div>

          <div class="nombre-archivo-wrapper" :class="{ 'nombre-archivo-wrapper--draggable': props.draggable }"
            :title="props.draggable ? $t('documentCard.dragToCopyHint') : undefined"
            :draggable="props.draggable"
            @dragstart="onDragStart($event, file)">
            <span class="nombre-archivo">{{ file.fileName }}</span>
          </div>
          <div class="iconos-derecha">
            <a v-if="props.actions.includes('replace')" @click="onDocumentReplace(file)" class="btn-link"> <i class="fa-regular fa-arrows-retweet fa-lg">
              </i></a>

            <a v-if="props.actions.includes('delete')" @click="onDeleteDocument(file)" class="btn-link"> <i class="fa-regular fa-trash fa-lg">
              </i></a>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { uniqueId } from 'lodash';
import { ref, computed, toRef } from 'vue';
import type { CardFile, CardFileRecord } from './models/CardFile';
import { useApi } from '@api/integration';
import { useModalService } from '@services/modal-service';
import { useIntl } from '@services/i18n/i18nservice';
import { toastError } from "@services/notification-service";
import { messageBoxResult } from '@components/dialogs/message-box';
import FileSaver from 'file-saver';
import DocumentViewer from './DocumentViewer.vue';
import { DxPopover, DxTextBox } from 'devextreme-vue';
import type { IDocumentType } from './models/IDocumentCardProps';
import { DxButton } from 'devextreme-vue/button';
import { DxSelectBox } from 'devextreme-vue/select-box';

const props = defineProps<{
  maxFiles: number;
  actions: Array<'open' | 'download' | 'delete' | 'replace' | 'add' | 'copy'>;
  viewerType: 'tab' | 'modal';
  files: CardFileRecord;
  allowedFileFormats: Array<'pdf' | 'image' | 'excel' | 'all'>;
  multiple: boolean;
  additionalDescriptionMode: 'none' | 'optional' | 'required';
  templateUri?: string;
  types?: IDocumentType[];
  initialTypeCode?: string;
  draggable: boolean;
}>();

const emit = defineEmits<{
  'deleted:document': [document: CardFile];
  'updated:document': [document: CardFile];
  'added:document': [document: CardFile];
  'selected:document-type': [typeCode: string];
  'drag-start:document': [document: CardFile];
}>();

const popoverInstanceId = uniqueId("dcb_");

const filesRef = toRef(props, "files") as unknown as { value: CardFileRecord };
const filesObject = computed<CardFileRecord>(() => {
  const files = Object.values(filesRef.value);
  const filteredFiles = selectedTypeCode.value
    ? files.filter(file => file.typeCode === selectedTypeCode.value)
    : files;
  return filteredFiles.toObject(x => x.id) as CardFileRecord ?? {};
});

const selectBoxProps = {
  dataSource: props.types,
  valueExpr: 'code',
  displayExpr: 'name',
  value: props.initialTypeCode,
} as DxSelectBox;

const selectedTypeCode = ref<string | null>(props.initialTypeCode ?? null);

const api = useApi();
const modal = useModalService();
const intl = useIntl();
let currentAction: "add" | "replace" | undefined;
const currentCardFile = ref<CardFile | null>(null);
const fileUploader = ref<HTMLInputElement>();

const numFiles = computed(() => Object.values(filesRef.value).filter(f => f.status !== "deleted").length);
const canAddFile = computed(() => {
  const hasRemainingFiles = props.maxFiles == null || numFiles.value < props.maxFiles;
  const mustSelectType = props.types && props.types.length > 1 && !selectedTypeCode.value;

  return hasRemainingFiles && !mustSelectType;
});

const acceptString = computed(() => {
  if (!props.allowedFileFormats || props.allowedFileFormats.length === 0) {
    return '*/*';
  }

  // si el user agent es un dispositivo móvil, aceptamos todo para que le pregunte si quiere usar la cámara o la galería
  if (navigator.userAgent.toLowerCase().includes('mobile')) {
    return '*/*';
  }

  const partes = props.allowedFileFormats.map((format) => {
    switch (format) {
      case 'pdf':
        return 'application/pdf,.pdf';
      case 'image':
        return 'image/*';
      case 'excel':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx';
      case 'all':
        return '*/*';
    }
  }).filter((x) => !!x) as string[];

  return partes.join(',');
});

const dxInputProps = computed(() => ({
  value: currentCardFile.value?.description,
  maxLength: 90
} as DxTextBox));

const validateFileExtension = (file: File): boolean => {
  const allowedFormats = props.allowedFileFormats || ['all'];

  if (!allowedFormats || allowedFormats.length === 0 || allowedFormats.includes('all')) {
    return true;
  }

  const mimeType = file.type.toLowerCase();

  return allowedFormats.some(format => {
    if (format === 'image') {
      return mimeType.startsWith('image/');
    }
    if (format === 'pdf') {
      return mimeType === 'application/pdf';
    }
    if (format === 'excel') {
      return mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    return false;
  });
};

const validateFileExtensionFromUri = (remoteUri: string): boolean => {
  const file = new File([], remoteUri.split('/').pop()!);
  return validateFileExtension(file);
}

const onFileInputChange = async (e: Event): Promise<void> => {

  const selectedFiles = (e.currentTarget as HTMLInputElement).files;

  if (!selectedFiles || selectedFiles.length === 0) {
    return;
  }

  const selectedFilesAsArray = Array.from(selectedFiles);

  const invalidFiles = selectedFilesAsArray.filter(file => !validateFileExtension(file));
  if (invalidFiles.length > 0) {
    toastError(intl.i18n.t('documentCard.validation.invalidType'));
    return;
  }

  let fileDescription: string | null | undefined = undefined;
  if (props.additionalDescriptionMode !== "none") {
    fileDescription = await modal.inputBox(intl.i18n.t('documentCard.description'), 'dxTextBox', dxInputProps.value, props.additionalDescriptionMode === "required");
  }

  if (fileDescription === undefined && props.additionalDescriptionMode === "required") {
    return;
  }

  if (currentAction === "add") {
    addFile(selectedFilesAsArray, fileDescription ?? null);
  } else if (currentAction === "replace") {
    replaceFile(currentCardFile.value!, selectedFilesAsArray[0], fileDescription ?? null);
  }
}

const onAddDocument = (): void => {
  currentAction = "add";
  currentCardFile.value = null;
  fileUploader.value!.value = "";
  if (props.multiple) {
    fileUploader.value!.setAttribute('multiple', '');
  }
  fileUploader.value!.click();
}

const addFile = (files: File | File[], description: string | null): void => {
  const filesArray = Array.isArray(files) ? files : [files];

  filesArray.forEach(file => {
    const newId = uniqueId("nuevo_");
    const newCardFile = {
      id: newId,
      file: file,
      fileName: file.name,
      remoteUri: null,
      uri: URL.createObjectURL(file),
      status: "new",
      description: description,
      typeCode: selectedTypeCode.value
    } as CardFile;

    filesRef.value![newId] = newCardFile;
    emit('added:document', newCardFile);
  });
}

const addFileFromUri = (remoteUri: string, fileName: string, description: string | null): void => {
  const newId = uniqueId("nuevo_");
  const newCardFile = {
    id: newId,
    remoteUri: remoteUri,
    fileName: fileName,
    status: "copied",
    description: props.additionalDescriptionMode !== "none" ? description : null,
    typeCode: selectedTypeCode.value
  } as CardFile;

  filesRef.value![newId] = newCardFile;
  emit('added:document', newCardFile);
}

const onDocumentReplace = (cardFile: CardFile): void => {
  currentAction = "replace";
  currentCardFile.value = cardFile;
  fileUploader.value!.value = "";
  fileUploader.value!.removeAttribute('multiple');
  fileUploader.value!.click();
}

const replaceFile = (cardFile: CardFile, file: File, description: string | null): void => {
  cardFile.file = file;
  cardFile.fileName = file.name;
  cardFile.uri = URL.createObjectURL(file);
  cardFile.status = (cardFile.remoteUri && cardFile.status !== "copied") ? "modified" : "new";
  cardFile.description = description;
  emit('updated:document', cardFile);
}

const openDocument = async (file: CardFile): Promise<void> => {

  var tempUrl = file.uri ?? await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, file.remoteUri!);

  if (props.viewerType === 'tab') {
    window.open(tempUrl, '_blank', 'noopener,noreferrer');
  }
  else {
    await modal.showModal(DocumentViewer, { url: tempUrl, fileName: file.fileName }, { title: file.fileName!, width: "auto" });
  }
}

const urlToBlob = async (ruta: string): Promise<Blob> => {
  const response = await fetch(ruta);
  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const data = await response.blob();
  const blob = new Blob([data], { type: contentType });
  return blob;
}

const downloadDocument = async (file: CardFile): Promise<void> => {

  if (!file.remoteUri) return;

  var tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, file.remoteUri);

  const blob = await urlToBlob(tempUrl);

  try {
    FileSaver.saveAs(blob, file.fileName!);
  }
  catch (error) {
    console.error('error', error);
  }
}

const deleteDocument = (file: CardFile): void => {
  if (file.remoteUri && file.status !== "copied") {
    file.status = "deleted";
  }
  else {
    delete filesRef.value[file.id];
  }

  emit('deleted:document', file);
}

const onDeleteDocument = async (file: CardFile): Promise<void> => {
  let respuesta = await modal.messageBox(intl.i18n.t('documentCard.confirmDeleteQuestion'), intl.i18n.t('documentCard.confirmDeleteTitle'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok) {
    deleteDocument(file);
  }
}

const deleteAllDocuments = (): void => {
  for (let file of Object.keys(filesRef.value)) {
    deleteDocument(filesRef.value[file]);
  }
}

const downloadTemplate = async (): Promise<void> => {
  var tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, props.templateUri);
  var blob = await urlToBlob(tempUrl);
  var file = props.templateUri!.split('/').pop()!;
  FileSaver.saveAs(blob, file);
}

const onSelectedTypeCodeChanged = (e: any): void => {
  emit('selected:document-type', e.value);
}

const dragImageRef = ref<HTMLElement | null>(null);
const dragImageFileNameRef = ref<HTMLElement | null>(null);

const onDragStart = (e: DragEvent, file: CardFile): void => {
  if (!e.dataTransfer) return;
  if (dragImageFileNameRef.value) {
    dragImageFileNameRef.value.textContent = file.fileName ?? "";
  }
  e.dataTransfer.effectAllowed = "copy";
  const payload = {
    id: file.id,
    fileName: file.fileName,
    remoteUri: file.remoteUri,
    description: file.description
  };
  e.dataTransfer.setData("application/json", JSON.stringify(payload));

  if (dragImageRef.value) {
    const el = dragImageRef.value;
    e.dataTransfer.setDragImage(el, el.offsetWidth / 2, el.offsetHeight / 2);
  }

  emit("drag-start:document", file);
};

const isDropHover = ref(false);
const dropZoneRef = ref<HTMLElement | null>(null);

const showDropOverlay = computed(() =>
  isDropHover.value && props.actions.includes("copy") && canAddFile.value
);

const onDragEnter = (): void => {
  isDropHover.value = true;
};

const onDragLeave = (e: DragEvent): void => {
  const zone = dropZoneRef.value;
  const related = e.relatedTarget as Node | null;
  if (!zone || !related || !zone.contains(related)) {
    isDropHover.value = false;
  }
};

const onDragOver = (e: DragEvent): void => {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
};

const onDrop = (e: DragEvent): void => {
  isDropHover.value = false;
  const json = e.dataTransfer?.getData("application/json");
  if (!json) return;
  const fileData = JSON.parse(json) as { id: string; fileName: string; remoteUri: string | null; description: string | null };

  if (!fileData.remoteUri) return;

  if (!props.actions.includes("copy") || !canAddFile.value) return;
  if (!validateFileExtensionFromUri(fileData.remoteUri)) {
    toastError(intl.i18n.t("documentCard.validation.invalidType"));
    return;
  }
  
  addFileFromUri(fileData.remoteUri!, fileData.fileName, fileData.description ?? null);
};

defineExpose({ deleteAllDocuments, numFiles });

</script>

<style lang="scss" scoped>


.document-card-body-drop-overlay {
   position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--background-primary-default), 0.95);
  border: 2px dashed var(--border-primary-default);
  border-radius: 8px;
  pointer-events: none;

  &__message {
    font-weight: 500;
    color: var(--text-primary-default);
  }
}

.add-icon-container-wrapper {
  padding-bottom: 1rem;
}

.document-card--horizontal .add-icon-container-wrapper {
  padding: 0.5rem 0;
}

.add-icon-container {
  font-size: 36px;
  font-weight: bold;
  color: var(--text-primary-default);
  cursor: pointer;
  border: 3px solid var(--border-primary-default);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  line-height: 64px;
  transition: all .2s;
}

.add-icon-container:hover {
  background-color: var(--button-default-default);
  color: var(--text-default-inverse);
}

.fila-archivos {
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 8px;

  .iconos-derecha,
  .iconos-izquieda {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nombre-archivo-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;

    &--draggable {
      cursor: grab;
      user-select: none;
      padding: 4px 6px;
      margin: -4px -6px;
      border-radius: 6px;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: rgba(var(--background-primary-default), 0.08);
      }

      &:active {
        cursor: grabbing;
      }
    }
  }

  .nombre-archivo__drag-handle {
    flex-shrink: 0;
    color: rgba(var(--text-primary-default), 0.45);
    font-size: 14px;
    pointer-events: none;
  }

  .fila-archivos--draggable:hover .nombre-archivo__drag-handle {
    color: var(--text-primary-default);
  }

  .nombre-archivo {
    word-wrap: break-word;
    word-break: break-word;
    flex: 1;
    min-width: 0;
  }
}

.fila-archivos .iconos-izquieda {
  flex: 0 0 auto;
  gap: 8px;
}

.document-file-list-container:has(.iconos-izquieda > a:nth-child(3) i) .iconos-izquieda {
  width: 76px;
  flex: 0 0 76px;
}

.btn-link {
  cursor: pointer;
}

.drag-ghost-image {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 180px;
  min-height: 80px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--background-surface-default);
  border: 2px solid var(--border-primary-default);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;

  i {
    font-size: 40px;
    color: var(--text-primary-default);
    flex-shrink: 0;
  }
}

.drag-ghost-image__filename {
  font-size: 12px;
  color: var(--text-default-default);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
</style>